import { useSyncExternalStore } from "react";
import type { DebugTab } from "../debug-panel.types";
import {
  getApiLogEntries,
  getServerSnapshotApiLog,
  subscribeApiLog,
} from "../stores/api-log-store";
import {
  getConnections,
  getServerSnapshotConnections,
  subscribeConnectionsStore,
} from "../stores/connections-store";
import {
  getErrorLogEntries,
  getServerSnapshotErrorLog,
  subscribeErrorLog,
} from "../stores/error-log-store";
import {
  getFormEntries,
  getServerSnapshotFormEntries,
  subscribeFormInspector,
} from "../stores/form-inspector-store";
import {
  getMissingKeys,
  getServerSnapshotMissingKeys,
  subscribeI18nStore,
} from "../stores/i18n-store";
import {
  getRenderCounts,
  getServerSnapshotRenderCounts,
  subscribeRenderCounterStore,
} from "../stores/render-counter-store";

export function useTabNoticeState() {
  const apiEntries = useSyncExternalStore(
    subscribeApiLog,
    getApiLogEntries,
    getServerSnapshotApiLog,
  );
  const errorEntries = useSyncExternalStore(
    subscribeErrorLog,
    getErrorLogEntries,
    getServerSnapshotErrorLog,
  );
  const missingKeys = useSyncExternalStore(
    subscribeI18nStore,
    getMissingKeys,
    getServerSnapshotMissingKeys,
  );
  const formEntries = useSyncExternalStore(
    subscribeFormInspector,
    getFormEntries,
    getServerSnapshotFormEntries,
  );
  const connections = useSyncExternalStore(
    subscribeConnectionsStore,
    getConnections,
    getServerSnapshotConnections,
  );
  const renderCounts = useSyncExternalStore(
    subscribeRenderCounterStore,
    getRenderCounts,
    getServerSnapshotRenderCounts,
  );
  const hasApiErrors = apiEntries.some(
    (entry) =>
      entry.status === 0 || (entry.status !== null && entry.status >= 400),
  );
  const hasDirtyForms = formEntries.some(
    (form) =>
      form.isDirty || !form.isValid || Object.keys(form.errors).length > 0,
  );
  const hasConnectionErrors = connections.some(
    (connection) => connection.errorCount > 0,
  );
  const hasHighRenders = renderCounts.some(
    (render) => render.status === "rapid" || render.count > 10,
  );

  return {
    visual: null,
    files: hasHighRenders ? "warning" : null,
    network: hasApiErrors ? "error" : apiEntries.length > 0 ? "info" : null,
    queries: null,
    session: null,
    sse: hasConnectionErrors ? "error" : connections.length > 0 ? "info" : null,
    errors: errorEntries.length > 0 ? "error" : null,
    i18n: missingKeys.length > 0 ? "error" : null,
    renders: null,
    permissions: null,
    forms: hasDirtyForms ? "error" : formEntries.length > 0 ? "info" : null,
    nav: null,
  } as Record<DebugTab, "error" | "warning" | "info" | null>;
}
